<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import ProductReferentialFilters from '#components/filter/ProductReferentialFilters.vue';
import TaxonomySelector from '#components/taxonomySelector/TaxonomySelector.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import FiltersActiveTags from '#domain/core/filter/components/FiltersActiveTags.vue';
import PeriodFilter from '#domain/core/filter/components/PeriodFilter.vue';
import type { ReferentialPeriodType } from '#domain/core/filter/components/periodSelector/periodSelector.model';
import StoresPerimeterSelector from '#domain/core/filter/components/StoresPerimeterSelector.vue';
import TaxValueSelector from '#domain/core/filter/components/TaxValueSelector.vue';
import LikeForLikeBanners from '#domain/performance/likeForLike/components/LikeForLikeBanners.vue';
import {
  type CarrefourReferentialIntervalType,
  type PerimeterFilterViewType,
  type ProductFilterScopeType
} from '@carrefour-gcs/shared';
import { computed, ref } from 'vue';

const {
  hasTaxButton = true,
  taxonomySelector = { onlyCarrefour: false },
  hasStorePerimeterSelector = true,
  perimeterFilterView = undefined,
  hasComparisonPeriod = false
} = defineProps<{
  hasTaxButton?: boolean;
  taxonomySelector?: { onlyCarrefour: boolean };
  hasStorePerimeterSelector?: boolean;
  perimeterFilterView?: PerimeterFilterViewType;
  referentialPeriod: ReferentialPeriodType;
  carrefourReferentialIntervals: keyof CarrefourReferentialIntervalType;
  hasComparisonPeriod?: boolean;
}>();

const { t } = useLanguage();

const { isDefaultPerimeter, resetPerimeter, productFilterConfig } = useKpisPerimeter();

const showFilters = ref(true);
const toggleFilter = () => {
  showFilters.value = !showFilters.value;
};

const productFilterScope = computed<ProductFilterScopeType>(() =>
  perimeterFilterView === 'competitorVisionZoomPage' ? 'competitorBase' : 'default'
);
</script>

<template>
  <div class="space-y-4 mt-4">
    <TaxonomySelector v-bind="taxonomySelector" />

    <!-- Filters zone -->
    <div class="flex items-center mt-4 gap-5 flex-wrap *:whitespace-nowrap w-full">
      <PeriodFilter
        :carrefour-referential-intervals="carrefourReferentialIntervals"
        :referential-period="referentialPeriod"
        :has-comparison-period="hasComparisonPeriod"
      />

      <TaxValueSelector v-if="hasTaxButton" />

      <StoresPerimeterSelector v-if="hasStorePerimeterSelector" />

      <span class="text-xs mr-1 text-gray-600 underline cursor-pointer" @click="toggleFilter">
        {{ showFilters ? t('commonLabel.hideFilters') : t('commonLabel.displayFilters') }}
      </span>
    </div>

    <hr />

    <div v-if="showFilters" class="flex items-center mt-4 gap-4 flex-wrap">
      <ProductReferentialFilters
        :product-filter-config="productFilterConfig[productFilterScope]"
        :perimeter-filter-view="perimeterFilterView"
      />
      <div class="flex-shrink-0">
        <ButtonBase
          icon-left="rotate-cw"
          :variant="'default'"
          :rounded="true"
          :disabled="isDefaultPerimeter()"
          :test-id="`reset-filters`"
          @click="resetPerimeter"
          >{{ t('commonLabel.updatePerimeter') }}</ButtonBase
        >
      </div>

      <div class="w-px h-[20px] border-r border-interactive-default-content-disabled"></div>

      <FiltersActiveTags
        :product-filter-config="productFilterConfig[productFilterScope]"
        :perimeter-filter-view="perimeterFilterView"
        :min-visible-items="3"
        :show-more-enabled="true"
      />
    </div>
  </div>

  <LikeForLikeBanners />
</template>
